import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Camera, Check, Copy, Loader2, Snowflake } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { useAuth } from '@/lib/auth/AuthContext';
import { useLocale } from '@/lib/i18n';
import { api, isApiError } from '@/lib/api';
import { readSnowPreference, persistSnowPreference, type SnowPreference } from '@/lib/snow';
import { Avatar } from '@/components/Avatar';
import { settingsTranslations } from './translations';

const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
import { API_BASE } from '@/lib/api-origin';

const formSchema = z.object({
  displayName: z.string().min(1, 'Required').max(64),
  username: z
    .string()
    .min(3, 'At least 3 characters')
    .max(32, 'At most 32 characters')
    .regex(USERNAME_REGEX, 'Letters, numbers, and underscores only'),
  bio: z.string().max(280).optional(),
  avatarUrl: z.string().url('Must be a valid URL').max(512).nullable().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function ProfileSettings() {
  const t = useSectionTranslation(settingsTranslations);
  const { user, refresh } = useAuth();
  const { locale, setLocale } = useLocale();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const copyTimerRef = useRef<number | null>(null);
  const [snowPref, setSnowPref] = useState<SnowPreference>(() => readSnowPreference());

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { displayName: '', username: '', bio: '', avatarUrl: null },
  });

  const avatarUrlValue = watch('avatarUrl');

  useEffect(() => {
    if (user) {
      reset({
        displayName: user.displayName,
        username: user.username,
        bio: user.bio ?? '',
        avatarUrl: user.avatarUrl ?? null,
      });
    }
  }, [user, reset]);

  useEffect(
    () => () => {
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    setSaved(false);
    setServerError(null);
    const payload = {
      displayName: values.displayName,
      username: values.username,
      bio: values.bio || '',
      avatarUrl: values.avatarUrl || null,
    };
    const res = await api.patch<{ user: unknown }>('/auth/profile', payload);
    setSaving(false);
    if (isApiError(res)) {
      setServerError(res.error?.message || t.profile.saveError);
      return;
    }
    setSaved(true);
    await refresh();
    setTimeout(() => setSaved(false), 2500);
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice still fires onChange.
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    if (!file.type.startsWith('image/') || isSvg) {
      setUploadError(t.profile.invalidType);
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setUploadError(t.profile.tooLarge);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('avatar', file);
      const res = await fetch(`${API_BASE}/auth/avatar`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const json = (await res.json().catch(() => null)) as {
        success: boolean;
        data?: { avatarUrl: string };
        error?: { message?: string };
      } | null;
      if (!res.ok || !json?.success || !json.data?.avatarUrl) {
        setUploadError(json?.error?.message || t.profile.uploadError);
        return;
      }
      const avatarUrl = json.data.avatarUrl;
      setValue('avatarUrl', avatarUrl, { shouldDirty: true });
      const patchRes = await api.patch('/auth/profile', { avatarUrl });
      if (isApiError(patchRes)) {
        setUploadError(patchRes.error?.message || t.profile.uploadError);
        return;
      }
      await refresh();
    } catch {
      setUploadError(t.profile.uploadError);
    } finally {
      setUploading(false);
    }
  };

  const copyUserId = async () => {
    const id = user?.userId;
    if (!id) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        const ta = document.createElement('textarea');
        ta.value = id;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — leave button unchanged */
    }
  };

  const displayAvatarUrl = avatarUrlValue || user?.avatarUrl || null;
  const copyLabel = copied ? t.profile.accountIdCopied : t.profile.copyAccountId;

  return (
    <div className="space-y-8 max-w-xl">
      <div>
        <h2 className="text-lg font-semibold text-brand-white mb-1">{t.profile.sectionTitle}</h2>
        <p className="text-sm text-brand-muted mb-6">{t.profile.sectionDesc}</p>

        {/* Avatar preview */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative shrink-0">
            <Avatar
              src={displayAvatarUrl}
              name={user?.displayName ?? '?'}
              size="md"
              className="h-16 w-16"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              aria-label={uploading ? t.profile.uploading : t.profile.avatar}
              title={uploading ? t.profile.uploading : t.profile.avatar}
              className="absolute bottom-0 right-0 h-7 w-7 flex items-center justify-center rounded-full bg-surface-card border border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
              onChange={handleFileChange}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-brand-white">{t.profile.avatar}</p>
            <p className="text-xs text-brand-muted">{t.profile.avatarHint}</p>
            {uploading && <p className="text-xs text-brand-muted mt-1">{t.profile.uploading}</p>}
          </div>
        </div>

        {user?.userId ? (
          <div className="flex items-center justify-between gap-3 rounded-xl bg-surface-card border border-surface-border px-4 py-3 mb-6">
            <div className="min-w-0">
              <p className="text-xs font-medium text-brand-muted">{t.profile.accountId}</p>
              <p className="text-sm text-brand-white font-mono truncate mt-0.5">{user.userId}</p>
            </div>
            <button
              type="button"
              onClick={copyUserId}
              aria-label={copyLabel}
              title={copyLabel}
              className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors"
            >
              {copied ? <Check size={14} className="text-brand-success" /> : <Copy size={14} />}
            </button>
          </div>
        ) : null}
        {copied && (
          <p className="text-xs text-brand-success -mt-4 mb-6" aria-live="polite">
            {t.profile.accountIdCopied}
          </p>
        )}

        {uploadError && (
          <div className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger mb-4" role="alert">
            {uploadError}
          </div>
        )}

        {serverError && (
          <div className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger mb-4" role="alert">
            {serverError}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.profile.displayName}</label>
            <input
              {...register('displayName')}
              placeholder={t.profile.displayNamePlaceholder}
              className="w-full rounded-lg bg-surface-card border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
            />
            {errors.displayName && <p className="text-xs text-brand-danger mt-1">{errors.displayName.message}</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.profile.username}</label>
            <input
              {...register('username')}
              placeholder={t.profile.usernamePlaceholder}
              className="w-full rounded-lg bg-surface-card border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
            />
            <p className="text-[11px] text-brand-muted mt-1">{t.profile.usernameHint}</p>
            {errors.username && <p className="text-xs text-brand-danger mt-1">{errors.username.message}</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.profile.avatarUrl}</label>
            <input
              {...register('avatarUrl')}
              placeholder={t.profile.avatarUrlPlaceholder}
              className="w-full rounded-lg bg-surface-card border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
            />
            <p className="text-[11px] text-brand-muted mt-1">{t.profile.avatarUrlHint}</p>
            {errors.avatarUrl && <p className="text-xs text-brand-danger mt-1">{errors.avatarUrl.message}</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.profile.bio}</label>
            <textarea
              {...register('bio')}
              rows={3}
              placeholder={t.profile.bioPlaceholder}
              className="w-full rounded-lg bg-surface-card border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors resize-none"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.profile.language}</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setLocale('en-US')}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  locale === 'en-US' ? 'border-accent/30 bg-accent/10 text-accent' : 'border-surface-border text-brand-muted hover:border-accent/30'
                }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLocale('ar-SA')}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  locale === 'ar-SA' ? 'border-accent/30 bg-accent/10 text-accent' : 'border-surface-border text-brand-muted hover:border-accent/30'
                }`}
              >
                العربية
              </button>
            </div>
          </div>

          <p className="text-xs text-brand-muted">{t.profile.cooldownReady}</p>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary h-10 disabled:opacity-60"
          >
            {saving ? (
              <><Loader2 size={15} className="animate-spin" />{t.profile.saving}</>
            ) : saved ? (
              <><Check size={15} />{t.profile.saved}</>
            ) : (
              <><Check size={15} />{t.profile.save}</>
            )}
          </button>
        </form>

        {/* Snowfall — a dark-mode seasonal effect the user can switch off. */}
        <div className="flex items-center justify-between gap-3 rounded-xl bg-surface-card border border-surface-border px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-accent">
              <Snowflake size={18} />
            </span>
            <div>
              <p className="text-sm font-medium text-brand-white">{t.snow.title}</p>
              <p className="text-xs text-brand-muted">{t.snow.desc}</p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={snowPref === 'on'}
            aria-label={t.snow.title}
            onClick={() => {
              const next: SnowPreference = snowPref === 'on' ? 'off' : 'on';
              setSnowPref(next);
              persistSnowPreference(next);
            }}
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
              snowPref === 'on' ? 'bg-accent' : 'bg-surface-border'
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                snowPref === 'on' ? 'left-[22px]' : 'left-0.5'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
